import { Trans } from "react-i18next";

import { ReactComponent as BedSvg } from "@/assets/icons/emojis/bed.svg";
import { ReactComponent as BrainSvg } from "@/assets/icons/emojis/brain.svg";
import { ReactComponent as CalendarSvg } from "@/assets/icons/emojis/calendar.svg";
import { ReactComponent as CalendarWithDateSvg } from "@/assets/icons/emojis/calendar-with-date.svg";
import { ReactComponent as ClockSvg } from "@/assets/icons/emojis/clock.svg";
import { ReactComponent as ClothesSvg } from "@/assets/icons/emojis/clothes.svg";
import { ReactComponent as ConfusedSvg } from "@/assets/icons/emojis/confused.svg";
import { ReactComponent as EnergySvg } from "@/assets/icons/emojis/energy.svg";
import { ReactComponent as ExcitedSvg } from "@/assets/icons/emojis/excited.svg";
import { ReactComponent as FamilySvg } from "@/assets/icons/emojis/family.svg";
import { ReactComponent as FireSvg } from "@/assets/icons/emojis/fire.svg";
import { ReactComponent as HomeSvg } from "@/assets/icons/emojis/home.svg";
import { ReactComponent as MoneySvg } from "@/assets/icons/emojis/money.svg";
import { ReactComponent as MoonSvg } from "@/assets/icons/emojis/moon.svg";
import { ReactComponent as PainSvg } from "@/assets/icons/emojis/pain.svg";
import { ReactComponent as ProgressSvg } from "@/assets/icons/emojis/progress.svg";
import { ReactComponent as RoutineSvg } from "@/assets/icons/emojis/routine.svg";
import { ReactComponent as StarSvg } from "@/assets/icons/emojis/star.svg";
import { ReactComponent as SunSvg } from "@/assets/icons/emojis/sun.svg";
import { ReactComponent as advancedSVG } from "@/assets/icons/fitness-level/advanced.svg";
import { ReactComponent as beginnerSVG } from "@/assets/icons/fitness-level/beginner.svg";
import { ReactComponent as intermediateSVG } from "@/assets/icons/fitness-level/intermediate.svg";
import { ReactComponent as enjoyableSVG } from "@/assets/icons/motivation/enjoyable.svg";
import { ReactComponent as inconvenientSVG } from "@/assets/icons/motivation/inconvenient.svg";
import { ReactComponent as necessarySVG } from "@/assets/icons/motivation/necessary.svg";
import { ReactComponent as basketballSVG } from "@/assets/icons/other-sports/basketball.svg";
import { ReactComponent as boxingSVG } from "@/assets/icons/other-sports/boxing.svg";
import { ReactComponent as cyclingSVG } from "@/assets/icons/other-sports/cycling.svg";
import { ReactComponent as yogaSVG } from "@/assets/icons/other-sports/yoga.svg";
import { getDateOneMonthFromNow } from "@/components/Questions/Date";
import {
    CardioMachinesEquipmentAnswersLabels,
    EventAnswersLabels,
} from "@/constants/answersLabels";
import {
    BodyInjures,
    MALE_BODY_INJURES_IMG,
    MALE_BODY_INJURES_IMG_SELECTED,
} from "@/constants/body-injures";
import {
    BodyMetricTypes,
    ImagesByBodyFatRange,
    MaleBodyFatRangeTypes,
} from "@/constants/bodyMetrics";
import { BackendBodyTypes, BodyTypes } from "@/constants/bodyType";
import { FitnessGoals, goalIngTextById, goalTextByIdChartImage } from "@/constants/fitnessGoal";
import { FitnessLevels, getFitnessLevelLabel } from "@/constants/fitnessLevels";
import { questionGroups } from "@/constants/questionGroups";
import { CheckboxType, QUESTIONS_IDS, QuestionTypes } from "@/constants/questionsIds";
import {
    CardioMachinesEquipment,
    Equipment,
    GymType,
    TrainingEnvironment,
} from "@/constants/trainingEnvironment";
import i18n from "@/localization/i18next";
import bodyArms from "@/male/assets/body-injures/arms.webp";
import bodyBack from "@/male/assets/body-injures/back.webp";
import bodyElbows from "@/male/assets/body-injures/elbows.webp";
import bodyFeet from "@/male/assets/body-injures/feet.webp";
import bodyKnees from "@/male/assets/body-injures/knees.webp";
import bodyLegs from "@/male/assets/body-injures/legs.webp";
import bodyNeck from "@/male/assets/body-injures/neck.webp";
import bodyNoIssues from "@/male/assets/body-injures/no-issues.webp";
import bodyShoulders from "@/male/assets/body-injures/shoulders.webp";
import ecto from "@/male/assets/body-type/ecto.webp";
import endo from "@/male/assets/body-type/endo.webp";
import meso from "@/male/assets/body-type/meso.webp";
import assaultBike from "@/male/assets/cardio-machines/assault-bike.webp";
import elliptical from "@/male/assets/cardio-machines/elliptical.webp";
import rower from "@/male/assets/cardio-machines/rowing-machine.webp";
import statBike from "@/male/assets/cardio-machines/stat-bike.webp";
import stepper from "@/male/assets/cardio-machines/stepper.webp";
import treadmill from "@/male/assets/cardio-machines/treadmill.webp";
import treadmillRunImg from "@/male/assets/cardio-machines/treadmill-run.webp";
import gym from "@/male/assets/location/gym.webp";
import home from "@/male/assets/location/home.webp";
import mixed from "@/male/assets/location/mixed.webp";
import muscleGain from "@/male/assets/new-qs-sequence/primary-goal/gain-muscle.webp";
import getInShape from "@/male/assets/new-qs-sequence/primary-goal/get-in-shape.webp";
import weightLoss from "@/male/assets/new-qs-sequence/primary-goal/lose-weight.webp";
import lean from "@/male/assets/target-body-type/lean.webp";
import muscular from "@/male/assets/target-body-type/muscular.webp";
import ripped from "@/male/assets/target-body-type/ripped.webp";
import weightBenchPress from "@/male/assets/weight-training/weight-bench-press.webp";
import weightDeadFit from "@/male/assets/weight-training/weight-dead-fit.webp";
import {
    AchieveFitnessGoalAnswers,
    ActivityEngagedAnswers,
    CopeStressAnswers,
    DailyWalkAnswers,
    DesiredWorkoutTimeAnswers,
    DietTypeAnswers,
    EatingHabitsAnswers,
    EventAnswers,
    FitnessExperienceAnswers,
    HealthProblemsAnswers,
    HopeToFeel,
    ImportantElementsAnswers,
    ManMotivationAnswers,
    OtherSportsAnswers,
    PushUpsSquartsAnswers,
    SleepAmountAnswers,
    SportPreferAnswers,
    SuccessMeasureAnswers,
    TrackStrengthAnswers,
    Train3MonthsAnswers,
    TrainingTriggerAnswers,
    WaterConsumption,
    YesNoAnswers,
} from "@/types/answers";
import { Genders } from "@/types/gender";
import { Question } from "@/types/question";
import { Answers } from "@/utils/AnswersProvider";

import bands from "../../assets/img/questions/equipment/bands.webp";
import brbll from "../../assets/img/questions/equipment/brbll.webp";
import dumbbell from "../../assets/img/questions/equipment/dumbbell.webp";
import kettleber from "../../assets/img/questions/equipment/kettleber.webp";
import mat from "../../assets/img/questions/equipment/mat.webp";
import none from "../../assets/img/questions/equipment/none.webp";
import pub from "../../assets/img/questions/equipment/pub.webp";
import rope from "../../assets/img/questions/equipment/rope.webp";
import WristBodyType from "../../components/WristBodyType";

export const getQuestions = (
    prefix = "man",
    answers: Answers,
    strapiQuestions?: Question<QuestionTypes.Radio | QuestionTypes.Checkbox>[] | null,
    isAndroid?: boolean,
    isNumberOfStepsExp?: boolean
) => [
    {
        id: QUESTIONS_IDS.UNHAPPY_ABOUT_YOUR_CURRENT_FITNESS,
        event: `${prefix}_${QUESTIONS_IDS.UNHAPPY_ABOUT_YOUR_CURRENT_FITNESS}`,
        type: QuestionTypes.Checkbox,
        checkboxType: CheckboxType.tileSquare,
        title: "What makes you most unhappy about your current fitness?",
        groupTitle: questionGroups.Goals,
        answers: [
            {
                id: "clothing_fit",
                text: "Clothing fit",
                svg: ClothesSvg,
            },
            {
                id: "lack_of_energy",
                text: "Lack of energy",
                svg: BedSvg,
            },
            {
                id: "not_seeing_progress",
                text: "Not seeing progress",
                svg: ConfusedSvg,
            },
            {
                id: "inconsistent_routine",
                text: "Inconsistent routine",
                svg: RoutineSvg,
            },
            {
                id: "weight_gain_despite_activity",
                text: "Weight gain despite activity",
                svg: ProgressSvg,
            },
            {
                id: "pain_and_discomfort",
                text: "Pain and discomfort",
                svg: PainSvg,
            },
        ],
    },
    {
        id: QUESTIONS_IDS.PRIMARY_GOAL,
        event: `${prefix}_${QUESTIONS_IDS.PRIMARY_GOAL}`,
        type: QuestionTypes.RadioImageLeft,
        title: "Which result would change your life the most?",
        groupTitle: questionGroups.Goals,
        answers: [
            {
                id: FitnessGoals.GainMuscle,
                text: "Build Muscle & Strength",
                description: "Get stronger and more powerful",
                img: muscleGain,
            },
            {
                id: FitnessGoals.LoseWeight,
                text: "Lose Weight & Burn Fat",
                description: "Drop dress sizes and feel lighter",
                img: weightLoss,
            },
            {
                id: FitnessGoals.GetInShape,
                text: "Get Fit & Look Toned",
                description: "Get in shape and see muscle definition",
                img: getInShape,
            },
        ],
    },
    {
        id: QUESTIONS_IDS.TRAIN_3_MONTHS,
        event: `${prefix}_${QUESTIONS_IDS.TRAIN_3_MONTHS}`,
        type: QuestionTypes.Radio,
        title: i18n.t("train_3months_prompt"),
        groupTitle: questionGroups.Fitness,
        answers: [
            {
                id: Train3MonthsAnswers.HardlyEver,
                text: i18n.t("hardly_ever"),
            },
            {
                id: Train3MonthsAnswers.One2PerWeek,
                text: i18n.t("1_2_per_week"),
            },
            {
                id: Train3MonthsAnswers.Three4PerWeek,
                text: i18n.t("3_4_per_week"),
            },
            {
                id: Train3MonthsAnswers.Five6PerWeek,
                text: i18n.t("5_6_per_week"),
            },
            {
                id: Train3MonthsAnswers.Everyday,
                text: i18n.t("everyday"),
            },
        ],
    },
    {
        id: QUESTIONS_IDS.DRIVES_TO_ACHIEVE_GOALS,
        event: `${prefix}_${QUESTIONS_IDS.DRIVES_TO_ACHIEVE_GOALS}`,
        type: QuestionTypes.Checkbox,
        checkboxType: CheckboxType.square,
        title: "What drives you to achieve your fitness goals?",
        groupTitle: questionGroups.Goals,
        answers: [
            {
                id: "confidence",
                text: "Be the most confident person in the room",
                isPopular: true,
            },
            {
                id: "clothing_fit",
                text: "Never worry about what to wear again",
            },
            {
                id: "no_limits",
                text: "Do any activity without physical limitations",
            },
            {
                id: "best_self",
                text: "Feel like your best self",
                isPopular: true,
            },
            {
                id: "become inspo",
                text: "Inspire others to improve their fitness",
            },
            {
                id: "other",
                text: "Other",
            },
        ],
    },
    {
        id: QUESTIONS_IDS.HOLDING_BACK_FROM_FITNESS_GOALS,
        event: `${prefix}_${QUESTIONS_IDS.HOLDING_BACK_FROM_FITNESS_GOALS}`,
        type: QuestionTypes.Checkbox,
        checkboxType: CheckboxType.square,
        title: "What's holding you back from your fitness goals?",
        subtitle: "Select all challenges that apply and we'll help you overcome them:",
        groupTitle: questionGroups.Goals,
        answers: [
            {
                id: "busy_schedule",
                text: "Busy schedule & long work hours",
                svg: ClockSvg,
            },
            {
                id: "motivation_boosts_to_train",
                text: "Need regular motivation boosts to train",
                svg: FireSvg,
            },
            {
                id: "no_workout_plan",
                text: "No workout plan or guidance",
                svg: CalendarSvg,
            },
            {
                id: "health_restrictions",
                text: "Injuries or health restrictions",
                svg: PainSvg,
            },
            {
                id: "family_responsibilities",
                text: "Family responsibilities & commitments",
                svg: FamilySvg,
            },
            {
                id: "limited_workout_space",
                text: "Limited workout space & equipment",
                svg: HomeSvg,
            },
            {
                id: "irregular_daily_routine",
                text: "Irregular daily routine",
                svg: SunSvg,
            },
            {
                id: "budget_limitations",
                text: "Budget limitations for fitness",
                svg: MoneySvg,
            },
        ],
    },
    {
        id: QUESTIONS_IDS.STATEMENT_TIRED_OF_GUESSING,
        type: QuestionTypes.Rate,
        event: `${prefix}_${QUESTIONS_IDS.STATEMENT_TIRED_OF_GUESSING}`,
        title: "How true is this for you?",
        groupTitle: questionGroups.Motivation,
        subtitle:
            "I want someone to just tell me exactly what exercises to do - I'm tired of guessing what's right.",
        maxRate: 5,
    },
    {
        id: QUESTIONS_IDS.ACTIVE_USERS_AND_REVIEWS,
        event: `${prefix}_${QUESTIONS_IDS.ACTIVE_USERS_AND_REVIEWS}`,
        type: QuestionTypes.ReviewAndActiveUsers,
        groupTitle: questionGroups.Goals,
    },
    {
        id: QUESTIONS_IDS.YOU_ARE_ON_THE_RIGHT_TRACK,
        event: `${prefix}_${QUESTIONS_IDS.YOU_ARE_ON_THE_RIGHT_TRACK}`,
        type: QuestionTypes.Checkbox,
        checkboxType: CheckboxType.square,
        title: "When will you know you're on the right track?",
        subtitle: "Pick your first exciting milestone",
        groupTitle: questionGroups.Goals,
        answers: [
            {
                id: "clothes_more_comfortable",
                text: "My clothes finally fit comfortably!",
                svg: ClothesSvg,
            },
            {
                id: "full_week_of_workouts",
                text: "Completed a full week of workouts!",
                svg: CalendarWithDateSvg,
            },
            {
                id: "climbing_stairs",
                text: "Climbed a flight of stairs without getting winded!",
                svg: StarSvg,
            },
            {
                id: "best_sleep",
                text: "Got my best sleep in months last night!",
                svg: MoonSvg,
            },
            {
                id: "feel_focused",
                text: "Actually feel focused and productive at work!",
                svg: BrainSvg,
            },
            {
                id: "someone_noticed_my_changes",
                text: "Someone just noticed changes in my body!",
                svg: ExcitedSvg,
            },
            {
                id: "more_energetic",
                text: "Feel more energetic throughout the day!",
                svg: EnergySvg,
            },
        ],
    },
    {
        id: QUESTIONS_IDS.DESIRED_BODY,
        type: QuestionTypes.RadioImageLeft,
        event: `${prefix}_${QUESTIONS_IDS.DESIRED_BODY}`,
        title: i18n.t("desired_body_prompt"),
        subtitle: i18n.t("target_body_motivation", {
            context: Genders.Male,
            defaultValue: "target_body_motivation",
        }),
        groupTitle: questionGroups.Goals,
        answers: [
            {
                id: BodyTypes.Lean,
                text: i18n.t("lean", {
                    context: Genders.Male,
                    defaultValue: "lean",
                }),
                img: lean,
                description: i18n.t("lean_description"),
            },

            {
                id: BodyTypes.Muscular,
                text: i18n.t("muscular"),
                description: i18n.t("muscular_description"),
                img: muscular,
            },
            {
                id: BodyTypes.Ripped,
                text: i18n.t("ripped"),
                description: i18n.t("ripped_description"),
                img: ripped,
            },
        ],
    },
    {
        id: QUESTIONS_IDS.SUCCESS_MEASURE,
        event: `${prefix}_${QUESTIONS_IDS.SUCCESS_MEASURE}`,
        type: QuestionTypes.Checkbox,
        title: i18n.t("success_measure_prompt"),
        subtitle: i18n.t("success_measure_motivation_prompt"),
        groupTitle: questionGroups.Goals,
        maxChosenOptions: 3,
        answers: [
            {
                id: SuccessMeasureAnswers.Muscle,
                text: i18n.t("muscle_gain"),
                description: i18n.t("building_muscle"),
            },
            {
                id: SuccessMeasureAnswers.Strength,
                text: i18n.t("increased_strength"),
                description: i18n.t("lifting_more_weight"),
            },
            {
                id: SuccessMeasureAnswers.Endurance,
                text: i18n.t("improved_endurance"),
                description: i18n.t("achieving_lower_body_fat"),
            },
            {
                id: SuccessMeasureAnswers.WeightLoss,
                text: i18n.t("weight_loss"),
                description: i18n.t("seeing_lower_numbers_on_scale"),
            },
            {
                id: SuccessMeasureAnswers.Measurements,
                text: i18n.t("body_measurements"),
                description: i18n.t("tracking_changes_in_size"),
            },
            {
                id: SuccessMeasureAnswers.Improvements,
                text: i18n.t("fitness_level_improvements"),
                description: i18n.t("progress_in_overall_fitness"),
            },
        ],
    },
    {
        id: QUESTIONS_IDS.TARGET_AREAS,
        event: `${prefix}_${QUESTIONS_IDS.TARGET_AREAS}`,
        type: QuestionTypes.CheckboxInline,
        title: i18n.t("focus_areas_prompt"),
        groupTitle: questionGroups.Goals,
        answers: [
            {
                id: BodyInjures.FullBody,
                text: i18n.t("full_body"),
                img: MALE_BODY_INJURES_IMG[BodyInjures.FullBody],
                selectedImg: MALE_BODY_INJURES_IMG_SELECTED[BodyInjures.FullBody],
            },
            {
                id: BodyInjures.Shoulders,
                text: i18n.t("shoulders"),
                img: MALE_BODY_INJURES_IMG[BodyInjures.Shoulders],
                selectedImg: MALE_BODY_INJURES_IMG_SELECTED[BodyInjures.Shoulders],
            },
            {
                id: BodyInjures.Arms,
                text: i18n.t("massive_biceps"),
                img: MALE_BODY_INJURES_IMG[BodyInjures.Arms],
                selectedImg: MALE_BODY_INJURES_IMG_SELECTED[BodyInjures.Arms],
            },
            {
                id: BodyInjures.Core,
                text: i18n.t("wider_back"),
                img: MALE_BODY_INJURES_IMG[BodyInjures.Core],
                selectedImg: MALE_BODY_INJURES_IMG_SELECTED[BodyInjures.Core],
            },
            {
                id: BodyInjures.Chest,
                text: i18n.t("chest"),
                img: MALE_BODY_INJURES_IMG[BodyInjures.Chest],
                selectedImg: MALE_BODY_INJURES_IMG_SELECTED[BodyInjures.Chest],
            },
            {
                id: BodyInjures.Abs,
                text: i18n.t("six_pack"),
                img: MALE_BODY_INJURES_IMG[BodyInjures.Abs],
                selectedImg: MALE_BODY_INJURES_IMG_SELECTED[BodyInjures.Abs],
            },
            {
                id: BodyInjures.Glutes,
                text: i18n.t("tight_glutes"),
                img: MALE_BODY_INJURES_IMG[BodyInjures.Glutes],
                selectedImg: MALE_BODY_INJURES_IMG_SELECTED[BodyInjures.Glutes],
            },
            {
                id: BodyInjures.Legs,
                text: i18n.t("strong_legs"),
                img: MALE_BODY_INJURES_IMG[BodyInjures.Legs],
                selectedImg: MALE_BODY_INJURES_IMG_SELECTED[BodyInjures.Legs],
            },
        ],
    },
    ...((answers?.[QUESTIONS_IDS.SUCCESS_MEASURE] as string[])?.includes("strength")
        ? [
              {
                  id: QUESTIONS_IDS.TRACK_STRENGTH,
                  event: `${prefix}_${QUESTIONS_IDS.TRACK_STRENGTH}`,
                  type: QuestionTypes.Checkbox,
                  title: i18n.t("track_strength_prompt"),
                  subtitle: i18n.t("track_strength_motivation_prompt"),
                  groupTitle: questionGroups.Goals,
                  answers: [
                      {
                          id: TrackStrengthAnswers.Weights,
                          text: i18n.t("heavier_weights"),
                          description: i18n.t("increasing_weight_lift"),
                      },
                      {
                          id: TrackStrengthAnswers.Milestones,
                          text: i18n.t("new_milestones"),
                          description: i18n.t("achieving_new_records"),
                      },
                      {
                          id: TrackStrengthAnswers.Reps,
                          text: i18n.t("more_repetitions"),
                          description: i18n.t("performing_higher_number_reps"),
                      },
                      {
                          id: TrackStrengthAnswers.CompoundExercises,
                          text: i18n.t("compound_exercises"),
                          description: i18n.t("improved_performance_exercises"),
                      },
                      {
                          id: TrackStrengthAnswers.Other,
                          text: i18n.t("other"),
                      },
                  ],
              },
          ]
        : []),

    {
        id: QUESTIONS_IDS.HOPE_TO_FEEL,
        event: `${prefix}_${QUESTIONS_IDS.HOPE_TO_FEEL}`,
        type: QuestionTypes.Checkbox,
        title: i18n.t("hope_to_feel_prompt"),
        groupTitle: questionGroups.Goals,
        answers: [
            {
                id: HopeToFeel.Energized,
                text: i18n.t("energized"),
                description: i18n.t("increased_energy_vitality"),
            },
            {
                id: HopeToFeel.Confident,
                text: i18n.t("confident"),
                description: i18n.t("confident_description"),
            },
            {
                id: HopeToFeel.Relaxed,
                text: i18n.t("relaxed"),
                description: i18n.t("relaxed_description"),
            },
            {
                id: HopeToFeel.Accomplished,
                text: i18n.t("accomplished"),
                description: i18n.t("accomplished_description"),
            },
        ],
        ...(isNumberOfStepsExp ? { progressBarBreakPoint: true } : {}),
    },
    {
        id: QUESTIONS_IDS.LOCATION,
        event: `${prefix}_${QUESTIONS_IDS.LOCATION}`,
        type: QuestionTypes.RadioImageLeft,
        title: i18n.t("choose_location"),
        groupTitle: questionGroups.WorkoutPreferences,
        answers: [
            {
                id: TrainingEnvironment.Home,
                text: i18n.t("home"),
                description: i18n.t("home_description"),
                img: home,
            },
            {
                id: TrainingEnvironment.Gym,
                text: i18n.t("gym"),
                description: i18n.t("gym_description"),
                img: gym,
            },
            {
                id: TrainingEnvironment.Mix,
                text: i18n.t("mix"),
                description: i18n.t("mixed_description"),
                img: mixed,
            },
        ],
    },
    {
        id: QUESTIONS_IDS.GYM_TYPE,
        event: `${prefix}_${QUESTIONS_IDS.GYM_TYPE}`,
        type: QuestionTypes.Radio,
        title: i18n.t("gym_type_prompt"),
        subtitle: i18n.t("equipment_suggestion"),
        groupTitle: questionGroups.WorkoutPreferences,
        answers: [
            {
                id: GymType.Large,
                text: i18n.t("large_gym"),
                description: i18n.t("large_gym_types"),
            },
            {
                id: GymType.Small,
                text: i18n.t("small_gym"),
                description: i18n.t("small_gym_types"),
            },
            {
                id: GymType.Garage,
                text: i18n.t("garage_gym"),
                description: i18n.t("garage_gym_types"),
            },
            {
                id: GymType.Home,
                text: i18n.t("home_gym"),
                description: i18n.t("home_gym_types"),
            },
        ],
    },
    {
        id: QUESTIONS_IDS.EQUIPMENT_BY_ENVIRONMENT,
        event: `${prefix}_${QUESTIONS_IDS.EQUIPMENT_BY_ENVIRONMENT}`,
        type: QuestionTypes.Checkbox,
        title: i18n.t("home_equipment_prompt"),
        groupTitle: questionGroups.WorkoutPreferences,
        answers: [
            {
                id: Equipment.Dumbbells,
                text: i18n.t("dumbbells"),
                img: dumbbell,
            },
            {
                id: Equipment.YogaMat,
                text: i18n.t("yoga_mat"),
                img: mat,
            },
            {
                id: Equipment.Barbell,
                text: i18n.t("barbell"),
                img: brbll,
            },
            {
                id: Equipment.JumpRope,
                text: i18n.t("jump_rope"),
                img: rope,
            },
            {
                id: Equipment.PullUps,
                text: i18n.t("pull_up_bar"),
                img: pub,
            },
            {
                id: Equipment.PowerBand,
                text: i18n.t("power_band"),
                img: bands,
            },
            {
                id: Equipment.Kettlebell,
                text: i18n.t("kettlebells"),
                img: kettleber,
            },
            {
                id: Equipment.No,
                text: i18n.t("none_of_the_above"),
                img: none,
            },
        ],
    },
    ...(!isAndroid
        ? [
              {
                  id: QUESTIONS_IDS.ADD_CARDIO_TO_PLAN,
                  event: `${prefix}_${QUESTIONS_IDS.ADD_CARDIO_TO_PLAN}`,
                  type: QuestionTypes.YesNoWithImage,
                  title: i18n.t("add_cardio_prompt"),
                  groupTitle: questionGroups.WorkoutPreferences,
                  img: treadmillRunImg,
                  answers: [
                      {
                          id: YesNoAnswers.Yes,
                          text: i18n.t("yes"),
                      },
                      {
                          id: YesNoAnswers.No,
                          text: i18n.t("not_now"),
                      },
                  ],
              },
              ...(answers[QUESTIONS_IDS.ADD_CARDIO_TO_PLAN] === YesNoAnswers.Yes
                  ? [
                        {
                            id: QUESTIONS_IDS.SELECT_CARDIO_MACHINES,
                            event: `${prefix}_${QUESTIONS_IDS.SELECT_CARDIO_MACHINES}`,
                            type: QuestionTypes.CheckboxImageLeft,
                            title: i18n.t("cardio_machines_equipment_prompt"),
                            groupTitle: questionGroups.WorkoutPreferences,
                            answers: [
                                {
                                    id: CardioMachinesEquipment.Treadmill,
                                    text: CardioMachinesEquipmentAnswersLabels[
                                        CardioMachinesEquipment.Treadmill
                                    ],
                                    img: treadmill,
                                },
                                {
                                    id: CardioMachinesEquipment.StationaryBike,
                                    text: CardioMachinesEquipmentAnswersLabels[
                                        CardioMachinesEquipment.StationaryBike
                                    ],
                                    img: statBike,
                                },
                                {
                                    id: CardioMachinesEquipment.AssaultBike,
                                    text: CardioMachinesEquipmentAnswersLabels[
                                        CardioMachinesEquipment.AssaultBike
                                    ],
                                    img: assaultBike,
                                },
                                {
                                    id: CardioMachinesEquipment.Elliptical,
                                    text: CardioMachinesEquipmentAnswersLabels[
                                        CardioMachinesEquipment.Elliptical
                                    ],
                                    img: elliptical,
                                },
                                {
                                    id: CardioMachinesEquipment.Stepper,
                                    text: CardioMachinesEquipmentAnswersLabels[
                                        CardioMachinesEquipment.Stepper
                                    ],
                                    img: stepper,
                                },
                                {
                                    id: CardioMachinesEquipment.Rower,
                                    text: CardioMachinesEquipmentAnswersLabels[
                                        CardioMachinesEquipment.Rower
                                    ],
                                    img: rower,
                                },
                            ],
                        },
                    ]
                  : []),
          ]
        : []),
    {
        id: QUESTIONS_IDS.IMPORTANT_ELEMENTS,
        event: `${prefix}_${QUESTIONS_IDS.IMPORTANT_ELEMENTS}`,
        type: QuestionTypes.Checkbox,
        title: i18n.t("important_elements_prompt"),
        subtitle: i18n.t("important_elements_motivation_prompt"),
        groupTitle: isNumberOfStepsExp
            ? questionGroups.WorkoutPreferences
            : questionGroups.WorkoutSettings,
        answers: [
            {
                id: ImportantElementsAnswers.Consistency,
                text: i18n.t("consistency"),
                description: i18n.t("consistency_description"),
            },
            {
                id: ImportantElementsAnswers.Commitment,
                text: i18n.t("commitment"),
                description: i18n.t("commitment_description"),
            },
            {
                id: ImportantElementsAnswers.Guidance,
                text: i18n.t("guidance"),
                description: i18n.t("guidance_description"),
            },
            {
                id: ImportantElementsAnswers.ExerciseVariety,
                text: i18n.t("exercise_variety"),
                description: i18n.t("exercise_variety_description"),
            },
            {
                id: ImportantElementsAnswers.LifestyleChanges,
                text: i18n.t("lifestyle_changes"),
                description: i18n.t("lifestyle_changes_description"),
            },
            {
                id: ImportantElementsAnswers.GoalFocus,
                text: i18n.t("goal_focus"),
                description: i18n.t("goal_focus_description", {
                    goal: goalIngTextById[answers?.[QUESTIONS_IDS.PRIMARY_GOAL] as FitnessGoals],
                }),
            },
            {
                id: ImportantElementsAnswers.Convenience,
                text: i18n.t("convenience"),
                description: i18n.t("convenience_description"),
            },
        ],
    },
    {
        id: QUESTIONS_IDS.SPORT_PREFER,
        event: `${prefix}_${QUESTIONS_IDS.SPORT_PREFER}`,
        type: QuestionTypes.Radio,
        title: i18n.t("workout_preference_prompt"),
        groupTitle: questionGroups.WorkoutPreferences,
        answers: [
            {
                id: SportPreferAnswers.Morning,
                text: i18n.t("morning"),
            },
            {
                id: SportPreferAnswers.Afternoon,
                text: i18n.t("afternoon"),
            },
            {
                id: SportPreferAnswers.Evening,
                text: i18n.t("evening"),
            },
            {
                id: SportPreferAnswers.Different,
                text: i18n.t("different_times"),
            },
        ],
    },
    {
        id: QUESTIONS_IDS.DESIRED_WORKOUT_TIME,
        event: `${prefix}_${QUESTIONS_IDS.DESIRED_WORKOUT_TIME}`,
        type: QuestionTypes.Radio,
        title: i18n.t("desired_workout_time_prompt"),
        groupTitle: questionGroups.WorkoutPreferences,
        answers: [
            {
                id: DesiredWorkoutTimeAnswers.Thirty,
                text: i18n.t("30_min_or_less"),
            },
            {
                id: DesiredWorkoutTimeAnswers.FortyFive,
                text: i18n.t("45_min_or_less"),
            },
            {
                id: DesiredWorkoutTimeAnswers.Sixty,
                text: i18n.t("one_hour_or_so"),
            },
            {
                id: DesiredWorkoutTimeAnswers.SixtyPlus,
                text: i18n.t("over_an_hour"),
            },
        ],
    },
    {
        id: QUESTIONS_IDS.OTHER_SPORTS,
        event: `${prefix}_${QUESTIONS_IDS.OTHER_SPORTS}`,
        type: QuestionTypes.Checkbox,
        title: i18n.t("other_sports_prompt"),
        groupTitle: questionGroups.WorkoutPreferences,
        answers: [
            {
                id: OtherSportsAnswers.No,
                text: i18n.t("no"),
            },
            {
                id: OtherSportsAnswers.Cardio,
                text: i18n.t("cardio"),
                description: i18n.t("cardio_types"),
                svg: cyclingSVG,
            },
            {
                id: OtherSportsAnswers.Flexibility,
                text: i18n.t("flexibility"),
                description: i18n.t("flexibility_types"),
                svg: yogaSVG,
            },
            {
                id: OtherSportsAnswers.MartialArts,
                text: i18n.t("martial_arts"),
                description: i18n.t("martial_arts_types"),
                svg: boxingSVG,
            },
            {
                id: OtherSportsAnswers.GameSports,
                text: i18n.t("game_sports"),
                description: i18n.t("game_sports_types"),
                svg: basketballSVG,
            },
        ],
    },
    {
        id: QUESTIONS_IDS.CORTISOL,
        type: "cortisol_screen",
        ...(isNumberOfStepsExp ? {} : { progressBarBreakPoint: true }),
        event: `${prefix}_cortisol`,
    },
    {
        id: QUESTIONS_IDS.BODY_TYPE,
        type: QuestionTypes.RadioImageLeft,
        event: `${prefix}_${QUESTIONS_IDS.BODY_TYPE}`,
        title: i18n.t("body_type_prompt"),
        subtitle: i18n.t("body_type_motivation_prompt"),
        groupTitle: questionGroups.Personal,
        answers: [
            {
                id: BackendBodyTypes.Skinny,
                text: i18n.t("ectomorph"),
                description: i18n.t("ectomorph_description"),
                img: ecto,
            },
            {
                id: BackendBodyTypes.Regular,
                text: i18n.t("mesomorph"),
                description: i18n.t("mesomorph_description"),
                img: meso,
            },
            {
                id: BackendBodyTypes.Extra,
                text: i18n.t("endomorph"),
                description: i18n.t("endomorph_description"),
                img: endo,
            },
        ],
        extraBottomComponent: <WristBodyType />,
    },
    {
        id: QUESTIONS_IDS.BODY_FAT,
        type: QuestionTypes.Range,
        event: `${prefix}_${QUESTIONS_IDS.BODY_FAT}`,
        groupTitle: questionGroups.Personal,
        title: i18n.t("body_fat_level_prompt"),
        answers: Object.values(MaleBodyFatRangeTypes).map((type) => ({
            id: type,
            value: type,
            img: ImagesByBodyFatRange[type],
        })),
    },
    {
        id: QUESTIONS_IDS.HEIGHT,
        event: `${prefix}_${QUESTIONS_IDS.HEIGHT}`,
        type: QuestionTypes.Input,
        inputType: BodyMetricTypes.HEIGHT,
        title: i18n.t("height_prompt"),
        groupTitle: questionGroups.Personal,
    },
    {
        id: QUESTIONS_IDS.CURRENT_WEIGHT,
        event: `${prefix}_${QUESTIONS_IDS.CURRENT_WEIGHT}`,
        type: QuestionTypes.Input,
        inputType: BodyMetricTypes.WEIGHT,
        title: i18n.t("current_weight_prompt"),
        groupTitle: questionGroups.Personal,
    },
    {
        id: QUESTIONS_IDS.TARGET_WEIGHT,
        event: `${prefix}_${QUESTIONS_IDS.TARGET_WEIGHT}`,
        type: QuestionTypes.Input,
        inputType: BodyMetricTypes.WEIGHT,
        weightType: "target",
        title: i18n.t("target_weight_prompt"),
        groupTitle: questionGroups.Personal,
    },
    {
        id: QUESTIONS_IDS.AGE,
        event: `${prefix}_${QUESTIONS_IDS.AGE}`,
        type: QuestionTypes.Input,
        inputType: BodyMetricTypes.AGE,
        title: i18n.t("age_prompt"),
        groupTitle: questionGroups.Personal,
    },
    ...((answers?.[QUESTIONS_IDS.AGE] as number) > 35
        ? [
              {
                  id: QUESTIONS_IDS.PROTECT_AGAINST_AGING,
                  event: `${prefix}_${QUESTIONS_IDS.PROTECT_AGAINST_AGING}`,
                  type: QuestionTypes.Radio,
                  title: i18n.t("protect_against_aging_prompt"),
                  groupTitle: isNumberOfStepsExp
                      ? questionGroups.Personal
                      : questionGroups.WorkoutSettings,
                  answers: [
                      {
                          id: YesNoAnswers.Yes,
                          text: i18n.t("yes"),
                      },
                      {
                          id: YesNoAnswers.No,
                          text: i18n.t("no"),
                      },
                  ],
              },
          ]
        : []),
    {
        id: QUESTIONS_IDS.FITNESS_LEVEL,
        event: `${prefix}_${QUESTIONS_IDS.FITNESS_LEVEL}`,
        type: QuestionTypes.Radio,
        title: i18n.t("fitness_level_prompt"),
        groupTitle: questionGroups.Fitness,
        answers: [
            {
                id: FitnessLevels.Beginner,
                text: getFitnessLevelLabel(FitnessLevels.Beginner, Genders.Male),
                description: i18n.t("beginner_description", {
                    context: Genders.Male,
                    defaultValue: "beginner_description",
                }),
                svg: beginnerSVG,
            },
            {
                id: FitnessLevels.Intermediate,
                text: getFitnessLevelLabel(FitnessLevels.Intermediate, Genders.Male),
                description: i18n.t("intermediate_description"),
                svg: intermediateSVG,
            },
            {
                id: FitnessLevels.Advanced,
                text: getFitnessLevelLabel(FitnessLevels.Advanced, Genders.Male),
                description: i18n.t("advanced_description"),
                svg: advancedSVG,
            },
        ],
    },
    {
        id: QUESTIONS_IDS.FITNESS_EXPERIENCE,
        event: `${prefix}_${QUESTIONS_IDS.FITNESS_EXPERIENCE}`,
        type: QuestionTypes.Checkbox,
        title: i18n.t("fitness_experience_prompt"),
        groupTitle: questionGroups.Fitness,
        answers: [
            {
                id: FitnessExperienceAnswers.PersonalTrainers,
                text: i18n.t("personal_trainers"),
            },
            {
                id: FitnessExperienceAnswers.FitnessApps,
                text: i18n.t("fitness_apps"),
            },
            {
                id: FitnessExperienceAnswers.WorkoutVideos,
                text: i18n.t("workout_videos"),
            },
            {
                id: FitnessExperienceAnswers.SelfTaught,
                text: i18n.t("self_taught"),
            },
            {
                id: FitnessExperienceAnswers.None,
                text: i18n.t("none"),
            },
        ],
    },
    {
        id: QUESTIONS_IDS.HEALTH_PROBLEMS,
        event: `${prefix}_${QUESTIONS_IDS.HEALTH_PROBLEMS}`,
        type: QuestionTypes.CheckboxImageLeft,
        title: i18n.t("injuries_prompt"),
        subtitle: i18n.t("injuries_motivation_prompt"),
        groupTitle: questionGroups.Fitness,
        emptyAnswer: {
            id: HealthProblemsAnswers.None,
            text: i18n.t("none"),
            img: bodyNoIssues,
        },
        answers: [
            {
                id: HealthProblemsAnswers.BackPain,
                text: i18n.t("back"),
                img: bodyBack,
            },
            {
                id: HealthProblemsAnswers.KneePain,
                text: i18n.t("knees"),
                img: bodyKnees,
            },
            {
                id: HealthProblemsAnswers.ShoulderPain,
                text: i18n.t("shoulders"),
                img: bodyShoulders,
            },
            {
                id: HealthProblemsAnswers.NeckPain,
                text: i18n.t("neck"),
                img: bodyNeck,
            },
            {
                id: HealthProblemsAnswers.ArmPain,
                text: i18n.t("arms"),
                img: bodyArms,
            },
            {
                id: HealthProblemsAnswers.ElbowPain,
                text: i18n.t("elbows"),
                img: bodyElbows,
            },
            {
                id: HealthProblemsAnswers.LegPain,
                text: i18n.t("legs"),
                img: bodyLegs,
            },
            {
                id: HealthProblemsAnswers.FootPain,
                text: i18n.t("feet"),
                img: bodyFeet,
            },
        ],
    },
    {
        id: QUESTIONS_IDS.ACTIVITY_ENGAGED,
        event: `${prefix}_${QUESTIONS_IDS.ACTIVITY_ENGAGED}`,
        type: QuestionTypes.Checkbox,
        title: i18n.t("activity_engaged_prompt"),
        subtitle: i18n.t("activity_engaged_motivation_prompt"),
        groupTitle: questionGroups.Fitness,
        answers: [
            {
                id: ActivityEngagedAnswers.NutritionChanges,
                text: i18n.t("nutrition_changes"),
                description: i18n.t("nutrition_changes_types"),
            },
            {
                id: ActivityEngagedAnswers.WeightTraining,
                text: i18n.t("weight_training"),
                description: i18n.t("weight_training_types"),
            },
            {
                id: ActivityEngagedAnswers.Cardio,
                text: i18n.t("cardio"),
                description: i18n.t("cardio_types"),
            },
            {
                id: ActivityEngagedAnswers.BodyweightTraining,
                text: i18n.t("bodyweight_training"),
                description: i18n.t("bodyweight_training_types"),
            },
            {
                id: ActivityEngagedAnswers.GroupSports,
                text: i18n.t("group_sports"),
                description: i18n.t("group_sports_types"),
            },
            {
                id: ActivityEngagedAnswers.MartialArts,
                text: i18n.t("martial_arts"),
                description: i18n.t("martial_arts_types"),
            },
            {
                id: ActivityEngagedAnswers.Other,
                text: i18n.t("other"),
            },
        ],
    },
    {
        id: QUESTIONS_IDS.PUSH_UPS,
        event: `${prefix}_${QUESTIONS_IDS.PUSH_UPS}`,
        type: QuestionTypes.Radio,
        title: i18n.t("push_ups_prompt"),
        groupTitle: questionGroups.Fitness,
        answers: [
            {
                id: PushUpsSquartsAnswers.Few,
                text: i18n.t("fewer_than_12"),
            },
            {
                id: PushUpsSquartsAnswers.Average,
                text: i18n.t("13_to_20"),
            },
            {
                id: PushUpsSquartsAnswers.Lot,
                text: i18n.t("more_than_20"),
            },
            {
                id: PushUpsSquartsAnswers.NoInfo,
                text: i18n.t("dont_know"),
            },
        ],
    },
    {
        id: QUESTIONS_IDS.SQUATS,
        event: `${prefix}_${QUESTIONS_IDS.SQUATS}`,
        type: QuestionTypes.Radio,
        title: i18n.t("squats_prompt"),
        groupTitle: questionGroups.Fitness,
        answers: [
            {
                id: PushUpsSquartsAnswers.Few,
                text: i18n.t("fewer_than_12"),
            },
            {
                id: PushUpsSquartsAnswers.Average,
                text: i18n.t("13_to_20"),
            },
            {
                id: PushUpsSquartsAnswers.Lot,
                text: i18n.t("more_than_20"),
            },
            {
                id: PushUpsSquartsAnswers.NoInfo,
                text: i18n.t("dont_know"),
            },
        ],
    },
    ...((answers?.[QUESTIONS_IDS.ACTIVITY_ENGAGED] as string[])?.includes("weight_training")
        ? [
              {
                  id: QUESTIONS_IDS.WEIGHT_DEAD_LIFT,
                  event: `${prefix}_${QUESTIONS_IDS.WEIGHT_DEAD_LIFT}`,
                  type: QuestionTypes.Input,
                  inputType: BodyMetricTypes.WEIGHT,
                  title: i18n.t("weight_dead_lift_prompt"),
                  groupTitle: questionGroups.Fitness,
                  img: weightDeadFit,
              },
              {
                  id: QUESTIONS_IDS.WEIGHT_BENCH_PRESS,
                  event: `${prefix}_${QUESTIONS_IDS.WEIGHT_BENCH_PRESS}`,
                  type: QuestionTypes.Input,
                  inputType: BodyMetricTypes.WEIGHT,
                  title: i18n.t("weight_bench_press_prompt"),
                  groupTitle: questionGroups.Fitness,
                  img: weightBenchPress,
              },
          ]
        : []),
    {
        id: QUESTIONS_IDS.BMI_RESULTS,
        type: QuestionTypes.BmiPromoMale,
        event: `${prefix}_${QUESTIONS_IDS.BMI_RESULTS}`,
        progressBarBreakPoint: true,
    },

    {
        id: QUESTIONS_IDS.MOTIVATION,
        event: `${prefix}_${QUESTIONS_IDS.MOTIVATION}`,
        type: QuestionTypes.Radio,
        title: i18n.t("motivation_prompt"),
        groupTitle: questionGroups.Lifestyle,
        answers: [
            {
                id: ManMotivationAnswers.Necessary,
                text: i18n.t("necessary"),
                description: i18n.t("necessary_description"),
                svg: necessarySVG,
            },
            {
                id: ManMotivationAnswers.Inconvenient,
                text: i18n.t("inconvenient"),
                description: i18n.t("inconvenient_description"),
                svg: inconvenientSVG,
            },
            {
                id: ManMotivationAnswers.Enjoyable,
                text: i18n.t("enjoyable"),
                description: i18n.t("enjoyable_description"),
                svg: enjoyableSVG,
            },
        ],
    },
    {
        id: QUESTIONS_IDS.EATING_HABITS,
        event: `${prefix}_${QUESTIONS_IDS.EATING_HABITS}`,
        type: QuestionTypes.Radio,
        title: i18n.t("eating_habits_prompt"),
        groupTitle: questionGroups.Lifestyle,
        answers: [
            {
                id: EatingHabitsAnswers.Emotional,
                text: i18n.t("emotional_eater"),
                description: i18n.t("emotional_eater_description"),
            },
            {
                id: EatingHabitsAnswers.Bored,
                text: i18n.t("bored_eater"),
                description: i18n.t("bored_eater_description"),
            },
            {
                id: EatingHabitsAnswers.Unconscious,
                text: i18n.t("unconscious_eater"),
                description: i18n.t("unconscious_eater_description"),
            },
            {
                id: EatingHabitsAnswers.Habitual,
                text: i18n.t("habitual_eater"),
                description: i18n.t("habitual_eater_description"),
            },
            {
                id: EatingHabitsAnswers.Energy,
                text: i18n.t("energy_eater"),
                description: i18n.t("energy_eater_description"),
            },
        ],
    },
    {
        id: QUESTIONS_IDS.COPE_STRESS,
        event: `${prefix}_${QUESTIONS_IDS.COPE_STRESS}`,
        type: QuestionTypes.Checkbox,
        title: i18n.t("cope_stress_prompt"),
        subtitle: i18n.t("cope_stress_motivation_prompt"),
        groupTitle: questionGroups.Lifestyle,
        answers: [
            {
                id: CopeStressAnswers.Exercising,
                text: i18n.t("exercising"),
            },
            {
                id: CopeStressAnswers.BeingCreative,
                text: i18n.t("being_creative"),
            },
            {
                id: CopeStressAnswers.BadHabits,
                text: i18n.t("bad_habits"),
            },
            {
                id: CopeStressAnswers.Eating,
                text: i18n.t("eating"),
            },
            {
                id: CopeStressAnswers.Outdoors,
                text: i18n.t("outdoors"),
            },
        ],
    },
    {
        id: QUESTIONS_IDS.SLEEP_AMOUNT,
        event: `${prefix}_${QUESTIONS_IDS.SLEEP_AMOUNT}`,
        type: QuestionTypes.Radio,
        title: i18n.t("sleep_prompt"),
        groupTitle: questionGroups.Lifestyle,
        answers: [
            {
                id: SleepAmountAnswers.LessThan5,
                text: i18n.t("fewer_than_5_hours"),
            },
            {
                id: SleepAmountAnswers.From5To6,
                text: i18n.t("5_to_6_hours"),
            },
            {
                id: SleepAmountAnswers.From7To8,
                text: i18n.t("7_to_8_hours"),
            },
            {
                id: SleepAmountAnswers.Over8,
                text: i18n.t("over_8_hours"),
            },
        ],
    },
    {
        id: QUESTIONS_IDS.DAILY_WALK,
        event: `${prefix}_${QUESTIONS_IDS.DAILY_WALK}`,
        type: QuestionTypes.Radio,
        title: i18n.t("daily_walk_prompt"),
        groupTitle: questionGroups.Lifestyle,
        answers: [
            {
                id: DailyWalkAnswers.Few,
                text: i18n.t("less_than_1_hour"),
            },
            {
                id: DailyWalkAnswers.Average,
                text: i18n.t("1_to_2_hours"),
            },
            {
                id: DailyWalkAnswers.Lot,
                text: i18n.t("more_than_2_hours"),
            },
        ],
    },
    {
        id: QUESTIONS_IDS.WATER_CONSUMPTION,
        type: QuestionTypes.Radio,
        event: `${prefix}_${QUESTIONS_IDS.WATER_CONSUMPTION}`,
        title: i18n.t("water_prompt"),
        groupTitle: questionGroups.Lifestyle,
        answers: [
            {
                id: WaterConsumption.NoWater,
                text: i18n.t("only_tea_coffee"),
            },
            {
                id: WaterConsumption.Low,
                text: i18n.t("fewer_than_2_glasses"),
            },
            {
                id: WaterConsumption.Average,
                text: i18n.t("2_to_6_glasses"),
            },
            {
                id: WaterConsumption.AboveAverage,
                text: i18n.t("7_to_10_glasses"),
            },
            {
                id: WaterConsumption.Lot,
                text: i18n.t("more_than_10_glasses"),
            },
        ],
    },
    {
        id: QUESTIONS_IDS.DIET_TYPE,
        type: QuestionTypes.Radio,
        event: `${prefix}_dietary_preferences`,
        title: i18n.t("dietary_preferences_prompt"),
        groupTitle: questionGroups.Lifestyle,
        answers: [
            {
                id: DietTypeAnswers.NoDiet,
                text: i18n.t("none"),
            },
            {
                id: DietTypeAnswers.Diabetes,
                text: i18n.t("diabetes"),
            },
            {
                id: DietTypeAnswers.Vegan,
                text: i18n.t("vegetarian_vegan"),
            },
            {
                id: DietTypeAnswers.GlutenFree,
                text: i18n.t("gluten_free"),
            },
            {
                id: DietTypeAnswers.LactoseFree,
                text: i18n.t("lactose_free"),
            },
            {
                id: DietTypeAnswers.NutAllergy,
                text: i18n.t("nut_allergy"),
            },
        ],
    },
    {
        id: QUESTIONS_IDS.TOO_EXPENSIVE_FITNESS_PROGRAM_FEE,
        event: `${prefix}_${QUESTIONS_IDS.TOO_EXPENSIVE_FITNESS_PROGRAM_FEE}`,
        title: (
            <Trans i18nKey="too_expensive_fitness_program_fee_prompt">
                <span className="text-zing-warm-neon-green-500" />
            </Trans>
        ),
        groupTitle: questionGroups.Lifestyle,
        subtitle: i18n.t("enter_amount"),
        type: QuestionTypes.CurrencyInput,
    },
    {
        id: QUESTIONS_IDS.PLAN_FAILED,
        type: QuestionTypes.Radio,
        event: `${prefix}_${QUESTIONS_IDS.PLAN_FAILED}`,
        title: (
            <Trans
                i18nKey="fitness_plan_failure_prompt"
                values={{
                    goal: goalTextByIdChartImage[
                        answers?.[QUESTIONS_IDS.PRIMARY_GOAL] as FitnessGoals
                    ],
                }}
            >
                <span className="text-zing-warm-neon-green-500" />
            </Trans>
        ),
        groupTitle: questionGroups.Lifestyle,
        answers: [
            {
                id: YesNoAnswers.Yes,
                text: i18n.t("yes"),
            },
            {
                id: YesNoAnswers.No,
                text: i18n.t("no"),
            },
        ],
    },
    {
        id: QUESTIONS_IDS.PROMO_HELP,
        ...(isNumberOfStepsExp ? {} : { progressBarBreakPoint: true }),
        event: `${prefix}_${QUESTIONS_IDS.PROMO_HELP}`,
        type: QuestionTypes.PromoHelp,
    },
    {
        id: QUESTIONS_IDS.TRAINING_TRIGGER,
        event: `${prefix}_${QUESTIONS_IDS.TRAINING_TRIGGER}`,
        type: QuestionTypes.Checkbox,
        title: i18n.t("training_trigger_prompt"),
        subtitle: i18n.t("training_trigger_motivation_prompt"),
        groupTitle: isNumberOfStepsExp ? questionGroups.Lifestyle : questionGroups.Motivation,
        answers: [
            {
                id: TrainingTriggerAnswers.JobRequirements,
                text: i18n.t("job_requirements"),
                description: i18n.t("job_requirements_description"),
            },
            {
                id: TrainingTriggerAnswers.ObservingInactivity,
                text: i18n.t("observing_inactivity"),
                description: i18n.t("observing_inactivity_description"),
            },
            {
                id: TrainingTriggerAnswers.VisualIndicators,
                text: i18n.t("visual_indicators"),
                description: i18n.t("visual_indicators_description"),
            },
            {
                id: TrainingTriggerAnswers.ParentalStamina,
                text: i18n.t("parental_stamina"),
                description: i18n.t("parental_stamina_description"),
            },
            {
                id: TrainingTriggerAnswers.ClothingFit,
                text: i18n.t("clothing_fit"),
                description: i18n.t("clothing_fit_description"),
            },
            {
                id: TrainingTriggerAnswers.HealthImprovement,
                text: i18n.t("health_improvement"),
                description: i18n.t("health_improvement_description"),
            },
            {
                id: TrainingTriggerAnswers.AgingConcerns,
                text: i18n.t("aging_concerns"),
                description: i18n.t("aging_concerns_description"),
            },
            {
                id: TrainingTriggerAnswers.CurrentFitnessLevels,
                text: i18n.t("current_fitness_levels"),
                description: i18n.t("current_fitness_levels_description"),
            },
            {
                id: TrainingTriggerAnswers.SexualPerformance,
                text: i18n.t("sexual_performance"),
                description: i18n.t("sexual_performance_description"),
            },
        ],
    },
    {
        id: QUESTIONS_IDS.EVENT_MOTIVATION,
        event: `${prefix}_${QUESTIONS_IDS.EVENT_MOTIVATION}`,
        type: QuestionTypes.Radio,
        title: (
            <Trans
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                i18nKey="important_event_prompt"
                values={{
                    goal: goalTextByIdChartImage[
                        answers?.[QUESTIONS_IDS.PRIMARY_GOAL] as FitnessGoals
                    ],
                }}
                context={Genders.Male}
                defaultValue={i18n.t("important_event_prompt")}
            >
                <span className="text-zing-warm-neon-green-500" />
            </Trans>
        ),
        subtitle: i18n.t("important_event_motivation"),
        groupTitle: isNumberOfStepsExp ? questionGroups.Lifestyle : questionGroups.Motivation,
        answers: [
            {
                id: EventAnswers.Vacation,
                text: EventAnswersLabels.vacation,
            },
            {
                id: EventAnswers.Wedding,
                text: EventAnswersLabels.wedding,
            },
            {
                id: EventAnswers.Birthday,
                text: EventAnswersLabels.birthday,
            },
            {
                id: EventAnswers.SchoolReunion,
                text: EventAnswersLabels.school_reunion,
            },
            {
                id: EventAnswers.FamilyGathering,
                text: EventAnswersLabels.family_gathering,
            },
            {
                id: EventAnswers.None,
                text: EventAnswersLabels.none,
            },
        ],
    },
    {
        id: QUESTIONS_IDS.EVENT_DATE,
        event: `${prefix}_${QUESTIONS_IDS.EVENT_DATE}`,
        type: QuestionTypes.Date,
        value: getDateOneMonthFromNow(),
        title:
            answers?.[QUESTIONS_IDS.EVENT_MOTIVATION] === EventAnswers.FamilyGathering
                ? i18n.t("event_timing_prompt")
                : answers?.[QUESTIONS_IDS.EVENT_MOTIVATION] === EventAnswers.Birthday
                  ? i18n.t("party_timing_prompt")
                  : i18n.t("conditional_event_timing_prompt", {
                        event: EventAnswersLabels[
                            answers?.[QUESTIONS_IDS.EVENT_MOTIVATION] as EventAnswers
                        ]?.toLowerCase(),
                    }),
        minDate: new Date().toISOString().split("T")[0],
        groupTitle: isNumberOfStepsExp ? questionGroups.Lifestyle : questionGroups.Motivation,
    },
    {
        id: QUESTIONS_IDS.STATEMENT_SUPPORT_BOOSTS_MOTIVATION,
        type: QuestionTypes.Rate,
        event: `${prefix}_${QUESTIONS_IDS.STATEMENT_SUPPORT_BOOSTS_MOTIVATION}`,
        title: i18n.t("rate_prompt"),
        groupTitle: isNumberOfStepsExp ? questionGroups.Lifestyle : questionGroups.Motivation,
        subtitle: isNumberOfStepsExp
            ? questionGroups.Lifestyle
            : i18n.t("statement_support_boosts_motivation"),
        maxRate: 5,
    },
    {
        id: QUESTIONS_IDS.ACHIEVE_FITNESS_GOAL,
        type: QuestionTypes.Radio,
        event: `${prefix}_${QUESTIONS_IDS.ACHIEVE_FITNESS_GOAL}`,
        title: i18n.t("achieve_fitness_goal_prompt"),
        groupTitle: isNumberOfStepsExp ? questionGroups.Lifestyle : questionGroups.Motivation,
        answers: [
            {
                id: AchieveFitnessGoalAnswers.HighlyConfident,
                text: i18n.t("highly_confident"),
                description: i18n.t("highly_confident_description"),
            },
            {
                id: AchieveFitnessGoalAnswers.ReasonablyConfident,
                text: i18n.t("reasonably_confident"),
                description: i18n.t("reasonably_confident_description"),
            },
            {
                id: AchieveFitnessGoalAnswers.Optimistic,
                text: i18n.t("optimistic"),
                description: i18n.t("optimistic_description"),
            },
            {
                id: AchieveFitnessGoalAnswers.NotConfident,
                text: i18n.t("not_confident"),
                description: i18n.t("not_confident_description"),
            },
        ],
    },
    ...(strapiQuestions ? strapiQuestions : []),
];
